import React, { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Sparkles, Send, X, Loader2 } from "lucide-react";
import { VoiceButton } from "./common";
import { AiCapSheet } from "./monetization";
import api from "../lib/api";
import { useLang } from "../i18n";
import { useLocation } from "react-router-dom";

export default function AiAssistant({ open, setOpen }) {
  const { t, lang } = useLang();
  const loc = useLocation();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const endRef = useRef(null);
  const sessionRef = useRef("sess-" + Math.random().toString(36).slice(2));

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: "ai", id: "welcome", text: lang === "hi" ? "नमस्ते! मैं साथी हूँ। आप क्या करना चाहते हैं — घर खोजना, किराया देना, या शिकायत करना?" : "Hi! I'm Sathi. What would you like to do — find a home, pay rent, or raise a complaint?" }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text, id: `u-${Date.now()}-${m.length}` }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/assistant", {
        message: text, language: lang || "en", session_id: sessionRef.current,
        context: `User is on the ${loc.pathname} screen.`,
      });
      if (data.capped) { setCapOpen(true); setBusy(false); return; }
      setMsgs((m) => [...m, { role: "ai", text: data.reply, id: `a-${Date.now()}-${m.length}` }]);
    } catch {
      setMsgs((m) => [...m, { role: "ai", text: "Sorry, please try again.", id: `a-${Date.now()}-${m.length}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" className="max-w-md mx-auto rounded-t-3xl p-0 h-[82vh] flex flex-col">
        <SheetTitle className="sr-only">Sathi AI Assistant</SheetTitle>
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-terracotta to-[#E05A47] text-white rounded-t-3xl">
          <div className="flex items-center gap-2 font-head font-bold"><Sparkles className="w-5 h-5" /> {t("nav_ai")}</div>
          <button onClick={() => setOpen(false)} data-testid="ai-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3 bg-sand" data-testid="ai-messages">
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-terracotta text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>{m.text}</div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-terracotta" /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="flex items-center gap-2 p-3 border-t bg-white">
          <VoiceButton onResult={(txt) => send(txt)} testId="ai-voice-btn" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={t("type_message")}
            data-testid="ai-input"
            className="flex-1 gs-tap rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-terracotta"
          />
          <button onClick={() => send()} data-testid="ai-send-btn" className="gs-tap w-12 flex items-center justify-center rounded-xl bg-terracotta text-white">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </SheetContent>
      <AiCapSheet open={capOpen} onOpenChange={setCapOpen} onUpgrade={() => setOpen(false)} />
    </Sheet>
  );
}
